import { useState } from "react";
import styled from "styled-components";
import colors from "../../../styles/colors";

const Container = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
`;

const ModalDialog = styled.div`
    position: relative;
    width: 100%;
    max-width: 500px;
    margin: 28px auto;
    pointer-events: none;
`;

const ModalContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: ${colors.bgColorBlack2};
    color: white;
    background-clip: padding-box;
    border: 5px solid #dc3545;
    border-radius: 5px;
    outline: 0;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #dee2e6;
`;

const ModalTitle = styled.h5`
    margin: 0;
    line-height: 1.5;
    font-size: 20px;
    font-weight: 500;
    color: #dc3545;
`;

const CloseButton = styled.button`
    box-sizing: content-box;
    width: 1em;
    height: 1em;
    padding: 4px;
    color: white;
    border: 0;
    border-radius: 4px;
    opacity: 0.5;
    background: transparent;
    cursor: pointer;
    font-size: 24px;
    font-weight: 700;
    line-height: 1;

    &:hover {
        opacity: 0.75;
    }

    &:before {
        content: "×";
    }
`;

const ModalBody = styled.div`
    position: relative;
    flex: 1 1 auto;
    padding: 16px;
    text-align: center;
`;

const ChampionInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin: 16px 0;

    img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 50%;
        border: 3px solid #dc3545;
    }

    .champion-name {
        font-size: 18px;
        font-weight: 600;
        color: gold;
    }

    .champion-date {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.7);
    }
`;

const WarningText = styled.p`
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    font-size: 16px;
`;

const ModalFooter = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    padding: 12px;
    border-top: 1px solid #dee2e6;
    gap: 8px;
`;

const Button = styled.button`
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    color: #ffffff;
    text-align: center;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent;
    padding: 6px 12px;
    font-size: 16px;
    border-radius: 4px;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;

    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
`;

const ButtonDanger = styled(Button)`
    color: white;
    background-color: #dc3545;
    border-color: #dc3545;

    &:hover:not(:disabled) {
        background-color: #bb2d3b;
        border-color: #b02a37;
    }
`;

const ButtonSecondary = styled(Button)`
    color: #ffffff;
    background-color: #6c757d;
    border-color: #6c757d;

    &:hover:not(:disabled) {
        background-color: #5c636a;
        border-color: #565e64;
    }
`;

const ErrorText = styled.p`
    margin-top: 12px;
    color: #dc3545;
    font-size: 14px;
`;

interface ChampionData {
    historyId?: number;
    wrestlerName: string;
    wrestlerAvatar?: string;
    dateWin: string;
}

interface DeleteChampionModaleProps {
    champion: ChampionData;
    beltId: number;
    onClose: () => void;
    onConfirm: (historyId: number, beltId: number) => Promise<void>;
}

function DeleteChampionModale({ champion, beltId, onClose, onConfirm }: DeleteChampionModaleProps) {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canDelete = champion.historyId !== undefined && champion.historyId !== null;

    const handleConfirm = async () => {
        if (!canDelete) {
            setError("Impossibile rimuovere: ID storico non disponibile");
            return;
        }
        setIsDeleting(true);
        setError(null);
        try {
            await onConfirm(champion.historyId!, beltId);
            onClose();
        } catch (err: any) {
            setError(err?.message || "Errore durante la rimozione del campione");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Container className="delete-champion-modale-container">
            <ModalDialog className="delete-champion-modale-dialog">
                <ModalContent className="delete-champion-modale-content">
                    <ModalHeader className="delete-champion-modale-header">
                        <ModalTitle className="delete-champion-modale-title">
                            Rimuovi Campione
                        </ModalTitle>
                        <CloseButton
                            type="button"
                            aria-label="Close"
                            onClick={onClose}
                            className="delete-champion-modale-close-button"
                        />
                    </ModalHeader>
                    <ModalBody className="delete-champion-modale-body">
                        <WarningText>
                            Sei sicuro di voler rimuovere questo campione dall'albo d'oro?
                        </WarningText>
                        <ChampionInfo className="delete-champion-modale-info">
                            {champion.wrestlerAvatar && (
                                <img
                                    src={process.env.REACT_APP_STORAGE_URL + champion.wrestlerAvatar}
                                    alt={champion.wrestlerName}
                                />
                            )}
                            <span className="champion-name">{champion.wrestlerName}</span>
                            <span className="champion-date">Vittoria: {champion.dateWin}</span>
                        </ChampionInfo>
                        <WarningText>
                            Questa azione non può essere annullata.
                        </WarningText>
                        {error && <ErrorText>{error}</ErrorText>}
                    </ModalBody>
                    <ModalFooter className="delete-champion-modale-footer">
                        <ButtonSecondary
                            type="button"
                            onClick={onClose}
                            disabled={isDeleting}
                        >
                            Annulla
                        </ButtonSecondary>
                        <ButtonDanger
                            type="button"
                            onClick={handleConfirm}
                            disabled={isDeleting}
                        >
                            {isDeleting ? "Rimozione..." : "Rimuovi"}
                        </ButtonDanger>
                    </ModalFooter>
                </ModalContent>
            </ModalDialog>
        </Container>
    );
}

export default DeleteChampionModale;
